import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { UserMembershipStatus } from '../../../../core/enums/users/membership-status';
import { UserRole } from '../../../../core/enums/users/role';
import { constructLicenseeName } from '../../../../modules/application/shared/helpers/licensee-name/licensee-name.helper';
import { ApplicationStateService } from '../../../../modules/application/shared/services/application-state/application-state.service';
import { UserInformationService } from '../../../../modules/user/shared/services/user-information/user-information.service';
import { UserStateService } from '../../../../modules/user/shared/services/user-state/user-state.service';

@Component({
    selector: 'app-application-header-info',
    templateUrl: './application-header-info.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AsyncPipe],
})
export class ApplicationHeaderInfoComponent {
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly userStateService = inject(UserStateService);
    private readonly userInformationService = inject(UserInformationService);

    readonly applicationState$ = this.applicationStateService.applicationState$;
    readonly licenseeName$ = this.applicationState$.pipe(
        map((applicationState) =>
            constructLicenseeName(
                applicationState?.licenseeFirstName ?? null,
                applicationState?.licenseeLastName ?? null,
                applicationState?.licenseeEntityName ?? null,
                applicationState?.applicantTypeCode ?? null,
            ),
        ),
    );

    /**
     * Determines if EWA Staff fields should be displayed in the application header.
     */
    shouldShowEwaStaffHeaderFields(): boolean {
        const user = this.userStateService.getUser();
        if (!user) {
            return false;
        }

        return user.role !== UserRole.Customer;
    }

    /**
     * Validates whether the user's membership status is part of the acceptable statuses.
     */
    isActiveUserMembershipStatus(
        userMembershipStatus: UserMembershipStatus | null | undefined,
    ): boolean {
        return this.userInformationService.isActiveUserMembershipStatus(
            userMembershipStatus,
        );
    }

    /**
     * Formats the user membership status label for display in the EWA Staff Application Header.
     */
    getFormattedUserMembershipStatusLabel(
        userMembershipStatus: UserMembershipStatus | null | undefined,
    ): string {
        return this.userInformationService.getMembershipStatusLabel(
            userMembershipStatus,
        );
    }
}
